<script setup>
import { computed, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('contacts/getUIFlags');
const labels = useMapGetter('labels/getLabels');
const isImportingContact = computed(() => uiFlags.value.isImporting);

const dialogRef = ref(null);
const fileInput = ref(null);
const selectedFile = ref(null);
const selectedFileName = ref('');
const csvHeaders = ref([]);
const sampleRow = ref([]);
const delimiter = ref(',');
const columnMapping = ref({});
const selectedLabels = ref([]);

const csvUrl = '/downloads/import-contacts-sample.csv';

const importFields = computed(() => [
  {
    key: 'name',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.NAME'),
    aliases: ['nome', 'name', 'nome_completo', 'cliente'],
  },
  {
    key: 'phone_number',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.PHONE'),
    aliases: [
      'telefone',
      'phone_number',
      'phone',
      'celular',
      'whatsapp',
      'fone',
    ],
  },
  {
    key: 'email',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.EMAIL'),
    aliases: ['email', 'e_mail'],
  },
  {
    key: 'cpf',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.CPF'),
    aliases: ['cpf', 'documento'],
  },
  {
    key: 'rg',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.RG'),
    aliases: ['rg', 'registro_geral'],
  },
  {
    key: 'matricula',
    label: t(
      'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.REGISTRATION'
    ),
    aliases: ['matricula', 'registration_number'],
  },
  {
    key: 'data_nascimento',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.BIRTH_DATE'),
    aliases: ['data_nascimento', 'nascimento', 'birth_date', 'date_of_birth'],
  },
  {
    key: 'nome_mae',
    label: t(
      'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.MOTHER_NAME'
    ),
    aliases: ['nome_mae', 'mae', 'mother_name'],
  },
  {
    key: 'margem_disponivel',
    label: t(
      'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.AVAILABLE_MARGIN'
    ),
    aliases: [
      'margem_disponivel',
      'margem',
      'available_margin',
      'valor_margem',
    ],
  },
  {
    key: 'observacao',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FIELDS.NOTES'),
    aliases: ['observacao', 'observacoes', 'nota', 'notas', 'notes'],
  },
]);

const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.title, label: label.title }))
);
const hasIdentityMapping = computed(
  () => columnMapping.value.phone_number || columnMapping.value.email
);
const canImport = computed(
  () =>
    selectedFile.value &&
    csvHeaders.value.length &&
    hasIdentityMapping.value &&
    selectedLabels.value.length
);

const normalizeColumnName = value =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const countDelimiter = (line, candidate) => {
  let count = 0;
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    if (line[index] === '"') quoted = !quoted;
    if (!quoted && line[index] === candidate) count += 1;
  }

  return count;
};

const detectDelimiter = line =>
  [',', ';', '\t'].reduce((best, candidate) =>
    countDelimiter(line, candidate) > countDelimiter(line, best)
      ? candidate
      : best
  );

const parseCsvRow = (line, separator) => {
  const values = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"' && quoted && line[index + 1] === '"') {
      value += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === separator && !quoted) {
      values.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }
  values.push(value.trim());
  return values;
};

const inferColumnMapping = headers =>
  importFields.value.reduce((mapping, field) => {
    const matchedHeader = headers.find(header =>
      field.aliases.includes(normalizeColumnName(header))
    );
    if (matchedHeader) mapping[field.key] = matchedHeader;
    return mapping;
  }, {});

const processFile = async file => {
  const text = (await file.slice(0, 64 * 1024).text()).replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  if (!lines.length) throw new Error('empty_file');

  delimiter.value = detectDelimiter(lines[0]);
  csvHeaders.value = parseCsvRow(lines[0], delimiter.value).filter(Boolean);
  sampleRow.value = lines[1] ? parseCsvRow(lines[1], delimiter.value) : [];
  if (!csvHeaders.value.length) throw new Error('missing_headers');

  columnMapping.value = inferColumnMapping(csvHeaders.value);
};

const reset = () => {
  selectedFile.value = null;
  selectedFileName.value = '';
  csvHeaders.value = [];
  sampleRow.value = [];
  delimiter.value = ',';
  columnMapping.value = {};
  selectedLabels.value = [];
  if (fileInput.value) fileInput.value.value = '';
};

const open = async () => {
  reset();
  if (!labels.value.length) await store.dispatch('labels/get');
  dialogRef.value?.open();
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  try {
    await processFile(file);
    selectedFile.value = file;
    selectedFileName.value = file.name;
  } catch (error) {
    reset();
    useAlert(t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.INVALID_FILE'));
  }
};

const handleRemoveFile = () => reset();

const sampleForHeader = header => {
  const index = csvHeaders.value.indexOf(header);
  return index >= 0 ? sampleRow.value[index] : '';
};

const uploadFile = () => {
  if (!canImport.value) return;

  emit('import', {
    file: selectedFile.value,
    columnMapping: columnMapping.value,
    labels: selectedLabels.value,
    delimiter: delimiter.value,
  });
};

const onSuccess = () => {
  dialogRef.value?.close();
  reset();
};

defineExpose({ dialogRef, open, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    overflow-y-auto
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE')"
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT')
    "
    :is-loading="isImportingContact"
    :disable-confirm-button="!canImport || isImportingContact"
    @confirm="uploadFile"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION') }}
        <a
          :href="csvUrl"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-11"
        >
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL')
          }}
        </a>
      </p>
    </template>

    <div class="flex flex-col gap-6">
      <div class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{
                selectedFileName ||
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.NO_FILE')
              }}
            </p>
            <p class="mb-0 mt-1 text-xs text-n-slate-11">
              {{
                t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FILE_FORMAT_HELP'
                )
              }}
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-1">
            <Button
              :label="
                selectedFile
                  ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHANGE')
                  : t(
                      'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE'
                    )
              "
              icon="i-lucide-upload"
              color="slate"
              variant="outline"
              size="sm"
              @click="handleFileClick"
            />
            <Button
              v-if="selectedFile"
              icon="i-lucide-trash"
              color="ruby"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".csv,text/csv,text/plain"
          class="hidden"
          @change="handleFileChange"
        />
      </div>

      <template v-if="selectedFile">
        <section class="flex flex-col gap-3">
          <div>
            <h4 class="mb-1 text-sm font-medium text-n-slate-12">
              {{
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING_TITLE')
              }}
            </h4>
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING_DESCRIPTION'
                )
              }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label
              v-for="field in importFields"
              :key="field.key"
              class="flex flex-col gap-1"
            >
              <span class="text-xs font-medium text-n-slate-12">
                {{ field.label }}
              </span>
              <select
                v-model="columnMapping[field.key]"
                class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option value="">
                  {{
                    t(
                      'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IGNORE_COLUMN'
                    )
                  }}
                </option>
                <option
                  v-for="header in csvHeaders"
                  :key="header"
                  :value="header"
                >
                  {{ header
                  }}{{
                    sampleForHeader(header)
                      ? ` — ${sampleForHeader(header)}`
                      : ''
                  }}
                </option>
              </select>
            </label>
          </div>

          <p
            v-if="!hasIdentityMapping"
            class="mb-0 rounded-lg bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
          >
            {{
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IDENTITY_REQUIRED'
              )
            }}
          </p>
        </section>

        <section class="flex flex-col gap-2">
          <div>
            <h4 class="mb-1 text-sm font-medium text-n-slate-12">
              {{
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_TITLE')
              }}
            </h4>
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_DESCRIPTION'
                )
              }}
            </p>
          </div>
          <TagMultiSelectComboBox
            v-model="selectedLabels"
            :options="labelOptions"
            :placeholder="
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_PLACEHOLDER'
              )
            "
            :search-placeholder="
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_SEARCH')
            "
            :empty-state="
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.NO_LABELS')
            "
          />
          <p v-if="!selectedLabels.length" class="mb-0 text-xs text-n-amber-11">
            {{
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABEL_REQUIRED')
            }}
          </p>
        </section>
      </template>
    </div>
  </Dialog>
</template>
